import { Button } from "@/components/ui/baseUi/button"
import { UserCellSkeleton } from "@/components/ui/layout/table/userCell"
import { Skeleton } from "@/components/ui/baseUi/skeleton"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { PencilEditIcon } from "@images/svgIcons/pencilEditIcon"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { Hourglass, Mail, Phone, Trash2 } from "lucide-react"

type BoxLeadOverviewSkeletonProps = {
  className?: string
} & React.ComponentProps<"div">

export function BoxLeadOverviewSkeleton({ className, ...props }: BoxLeadOverviewSkeletonProps) {
  return (
    <div className={cn("flex w-full flex-col gap-8 px-5 pt-5 pb-8", className)} {...props}>
      {/* HEADER */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4">
          <div className="flex flex-col gap-1">
            <Text fontStyle="LS" className="text-textDefault-caption">
              {word("box.lead.overview.content.firstContact.label").toUpperCase()}
            </Text>
            <Skeleton className="h-4 w-2/3 rounded" />
          </div>
        </div>
        <div className="col-span-4">
          <div className="flex flex-row items-center gap-1">
            <Hourglass size={24} color="currentColor" strokeWidth={1.5} />
            <Skeleton className="h-4 w-2/3 rounded" />
          </div>
        </div>
        <div className="col-span-4">
          <div className="flex flex-col gap-1">
            <Text fontStyle="LS" className="text-textDefault-caption">
              {word("box.lead.overview.content.madeBy.label").toUpperCase()}
            </Text>
            <UserCellSkeleton subtitle={true} />
          </div>
        </div>
      </div>

      {/* CONTACT */}
      <div className="flex flex-col gap-2">
        <Text fontStyle="TM" className="text-textDefault-body">
          {word("global.form.label.contact")}
        </Text>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-7 flex flex-row items-center gap-2">
            <Mail strokeWidth={1.5} color="currentColor" size={22} />
            <Skeleton className="h-4 w-full rounded" />
          </div>
          <div className="col-span-5 flex flex-row items-center gap-2">
            <Phone strokeWidth={1.5} color="currentColor" size={22} />
            <Skeleton className="h-4 w-full rounded" />
          </div>
        </div>
      </div>

      {/* GOALS AND EXPERIENCE LEVEL */}
      <div className="flex flex-row justify-between">
        <div className="flex w-5/12 flex-col gap-2">
          <Text fontStyle="LS" className="text-textDefault-caption">
            {word("box.lead.overview.content.goals.label", { count: 1 }).toUpperCase()}
          </Text>
          <Skeleton className="h-4 w-full rounded" />
        </div>
        <div className="flex w-3/12 flex-col gap-2">
          <Text fontStyle="LS" className="text-textDefault-caption">
            {word("box.lead.overview.content.experienceLevel.label").toUpperCase()}
          </Text>
          <Skeleton className="h-4 w-full rounded" />
        </div>
      </div>

      {/* HEARD ABOUT US */}
      <div className="flex flex-col gap-2">
        <Text fontStyle="LS" className="text-textDefault-caption">
          {word("box.lead.overview.content.hearAboutUs.label").toUpperCase()}
        </Text>
        <Skeleton className="h-4 w-1/3 rounded" />
      </div>

      {/* BEST TIME TO CALL */}
      <div className="flex flex-col gap-2">
        <Text fontStyle="LS" className="text-textDefault-caption">
          {word("box.lead.overview.content.bestTimeToContact.label").toUpperCase()}
        </Text>
        <Skeleton className="h-4 w-2/3 rounded" />
      </div>

      {/* INTERNAL NOTE*/}
      <div className="flex flex-col gap-2">
        <Text fontStyle="TM" className="text-textDefault-body">
          {word("global.form.label.internalNote")}
        </Text>
        <div className="mt-2 mb-2 flex flex-row gap-2">
          <WarningIcon color="currentColor" width={24} height={24} className="w-1/12 text-textDefault-body" />
          <Text fontStyle="BS" className="text-textDefault-caption">
            {word("box.lead.form.input.internalNote.warning")}
          </Text>
        </div>
        <Skeleton className="h-28 w-full rounded" />
      </div>

      {/* BUTTONS */}
      <div className="mb-6 flex w-full flex-row items-center justify-center gap-2">
        <Button variant="outline" size="lg" className="grow-1" disabled={true}>
          <Trash2 color="currentColor" size={15} />
          {word("box.lead.overview.content.button.deleteLead")}
        </Button>
        <Button variant="primary" size="lg" className="grow-1" disabled={true}>
          <PencilEditIcon color="currentColor" size={15} />
          {word("global.edit")}
        </Button>
      </div>
    </div>
  )
}
